import { computeMaximumCouponForGoodsList, sortCoupon } from 'services/coupon/get-maximum-coupon';
import urlParams from 'libs/query';
import apiActionWrapper from 'actions/api';
import { get } from 'libs/request';
import { IOriginCoupon, ICouponApplied } from 'types/coupon';
import { mapAppliedCoupons, mapNotApplyCoupons } from 'services/coupon/map-coupon-data';
import {
    getAvailableCoupon,
    getOtherShopCoupon,
    getPlatformCoupon,
    getSelfShopCoupon,
    getShopCoupon,
    getUnavailableCoupon
} from 'services/coupon/get-coupon-list';
import { ICampaign } from 'types/campaign';
import { changeCouponStatus, changeMultiShopStatus } from 'services/coupon/change-coupon-status';
import { ApiError } from 'types/api-error';
import { CartItem, CartShop, CouponGoodsItem, PriceInfo } from 'types/cart';
import { initComputeCartPrice } from 'services/cart/compute-cart-price';
import {
    classifyCouponByShop,
    classifySelectedCouponByShop
} from 'services/coupon/classify-coupon-by-shop';
import { API_EC, API_ORIGIN } from 'src/config/host';
import { sendEvent } from 'libs/event';
import { getCouponIdObj } from 'libs/get-coupn-id';
import { getCouponScope } from 'services/coupon/type';
import { couponScopeConst } from 'config/coupon';

export const GET_COUPON_LIST = {
    REQUEST: 'GET_COUPON_LIST_REQUEST',
    SUCCESS: 'GET_COUPON_LIST_SUCCESS',
    FAILURE: 'GET_COUPON_LIST_FAILURE'
};

export const FETCH_CART_COUPON_LIST = {
    REQUEST: 'FETCH_CART_COUPON_LIST_REQUEST',
    SUCCESS: 'FETCH_CART_COUPON_LIST_SUCCESS',
    FAILURE: 'FETCH_CART_COUPON_LIST_FAILURE'
};
export const UPDATE_COUPON_SELECT_CHANGE = 'UPDATE_COUPON_SELECT_CHANGE';
export const UPDATE_COUPON_SELECT_CHANGE_NEW = 'UPDATE_COUPON_SELECT_CHANGE_NEW';

export type ISelectedCoupon = Record<string, ICouponApplied | undefined>;
export type ISelectedCouponId = Record<string, string>;

export interface CouponState {
    allCoupons: ICouponApplied[];
    available: ICouponApplied[]; // 可能能用到的优惠券
    unAvailable?: ICouponApplied[]; // 彻底不能用的优惠券
    otherCoupon?: ICouponApplied[]; // 别的店铺的店铺优惠券（购物车会用到）
    selected: ISelectedCoupon;
    selectedIds: ISelectedCouponId;
    availableIds: string[];
    bestSelectedIds: ISelectedCouponId;
    maxDiscount: ISelectedCoupon;
    failure?: ApiError;
}

export interface WithCouponState {
    coupon: CouponState;
}

export const COUPON_FACTORS_CHANGED = 'COUPON_FACTORS_CHANGED';
export const CART_COUPON_INIT = 'CART_COUPON_INIT';
export const SET_BEST_CART_COUPON = 'SET_BEST_CART_COUPON';
export const SET_AVAILABLE_COUPON = 'SET_AVAILABLE_COUPON';
export interface CouponFactors {
    price: number;
    discount: number;
    campaign?: ICampaign;
    fullDiscount?: ICampaign;
    payType: number;
    shopId: string;
    supplyStatus: number;
    goods: CouponGoodsItem;
    isSingle: boolean;
}

function cartCouponDataInit(data: { plat_coupon: IOriginCoupon[]; shop_coupon: IOriginCoupon[] }) {
    // const newData = data.shop_coupon || {};
    // newData.platform = [...(data.plat_coupon || [])];

    // return Object.values(newData).reduce((item1, item2) => {
    //     return item1.concat(item2);
    // }, []);
    return [...data.plat_coupon.map(mapAppliedCoupons), ...data.shop_coupon.map(mapAppliedCoupons)];
}

function initCartCouponList(
    {
        list,
        selected,
        restrictIds,
        priceInfo
    }: { list: CartShop[]; selected: string[]; restrictIds: string[]; priceInfo: PriceInfo },
    couponList: ICouponApplied[]
): Partial<CouponState> {
    const goods: CartItem[] = [];
    const shopSet = new Set<string>([]);
    list.forEach((shop) => {
        shop.items.forEach((goodsItem) => {
            if (selected.indexOf(goodsItem.cartId) > -1) {
                goods.push({ ...goodsItem });
                shopSet.add(goodsItem.shopId);
            }
        });
    });

    const supplyId = goods.map((item) => {
        return { supplyStatus: item.supplyId, shopId: item.shopId };
    });
    const payType = goods.map((item) => item.payType);
    const goodsId = goods.map((item) => item.id);
    const campaign = goods.map((item) => item.campaign);
    const { selectedCoupon, available: sortCoupon, goods: goodsCoupon } = initComputeCartPrice(
        selected,
        list,
        couponList,
        false,
        restrictIds,
        priceInfo
    );
    const selfCoupon = getSelfShopCoupon(sortCoupon, Array.from(shopSet));
    const unAvailable = getUnavailableCoupon(
        selfCoupon,
        Array.from(shopSet),
        supplyId,
        payType,
        goodsId,
        campaign
    );
    const available = getAvailableCoupon(
        selfCoupon,
        Array.from(shopSet),
        supplyId,
        payType,
        goodsId,
        campaign
    );
    const classifyCoupon = classifyCouponByShop(available);
    Object.keys(classifyCoupon).forEach((shopId) => {
        if (shopId !== 'platform') {
            getShopCoupon(
                classifyCoupon[shopId],
                goodsCoupon.filter((item) => item.shopId === shopId)
            );
        }
    });
    if (classifyCoupon.platform) {
        getPlatformCoupon(classifyCoupon.platform, goodsCoupon);
    }
    if (selectedCoupon) {
        changeMultiShopStatus(selfCoupon, selectedCoupon, goodsCoupon, false);
    }
    return {
        unAvailable: [...unAvailable],
        available: [...available],
        otherCoupon: [...getOtherShopCoupon(couponList, Array.from(shopSet))],
        selected: selectedCoupon ? { ...selectedCoupon } : {},
        maxDiscount: selectedCoupon ? { ...selectedCoupon } : {}
    };
}

export default (
    state: CouponState = {
        allCoupons: [],
        available: [],
        selected: {},
        maxDiscount: {},
        bestSelectedIds: {},
        selectedIds: {},
        availableIds: []
    },
    action: any
): CouponState => {
    switch (action.type) {
        case GET_COUPON_LIST.REQUEST: {
            return { ...state, failure: undefined };
        }
        case GET_COUPON_LIST.SUCCESS: {
            const factors = action.payload.factors;
            const couponList = Array.prototype.map.call(action.payload, mapAppliedCoupons);
            if (!factors) {
                return {
                    ...state,
                    available: couponList as ICouponApplied[]
                };
            }
            const unAvailable = sortCoupon(
                getUnavailableCoupon(
                    couponList as ICouponApplied[],
                    [factors.shopId],
                    [{ shopId: factors.shopId, supplyStatus: factors.supplyStatus }],
                    [factors.payType],
                    [urlParams.id],
                    [factors.campaign]
                )
            );
            const available = getAvailableCoupon(
                couponList as ICouponApplied[],
                [factors.shopId],
                [{ shopId: factors.shopId, supplyStatus: factors.supplyStatus }],
                [factors.payType],
                [urlParams.id],
                [factors.campaign]
            );
            const sortedList = initCouponState(available, factors);
            const doPointObj = getCouponIdObj(sortedList.selected);
            sendEvent('special_page_view', doPointObj);
            return {
                ...state,
                unAvailable,
                ...sortedList
            };
        }
        case UPDATE_COUPON_SELECT_CHANGE_NEW: {
            let selectedIds = Object.assign({}, state.selectedIds);
            const coupon = action.payload;
            if (!coupon) {
                // 选择最优，把自己选的清除掉
                return {
                    ...state,
                    selectedIds: state.bestSelectedIds
                };
            }
            if (getCouponScope(coupon) === couponScopeConst.platform) {
                if (coupon.selected) {
                    selectedIds['platform'] = coupon?.id;
                } else {
                    delete selectedIds['platform'];
                }
            } else {
                if (coupon.selected) {
                    selectedIds[coupon.shopId] = coupon?.id;
                } else {
                    delete selectedIds[coupon.shopId];
                }
            }
            return {
                ...state,
                selectedIds
            };
        }
        case UPDATE_COUPON_SELECT_CHANGE: {
            const factors = action.payload.factors;
            if (!factors) return state;
            const list = changeCouponStatus(
                [...state.available],
                action.payload.coupon,
                factors.price,
                factors.discount
            );

            const selectObj = classifySelectedCouponByShop(list, factors.isSingle);

            return {
                ...state,
                available: list,
                selected: selectObj
            };
        }
        case GET_COUPON_LIST.FAILURE:
            return { ...state, failure: action.payload };
        case FETCH_CART_COUPON_LIST.SUCCESS: {
            return {
                ...state,
                allCoupons: [...cartCouponDataInit(action.payload)],
                available: [...cartCouponDataInit(action.payload)],
                unAvailable: undefined,
                otherCoupon: undefined,
                maxDiscount: {},
                selected: {}
            };
        }
        case COUPON_FACTORS_CHANGED: {
            const factors = action.payload;
            if (!factors) return state;
            if (!state.unAvailable) {
                if (state.available.length === 0) return { ...state, unAvailable: [] };
                const unAvailable = sortCoupon(
                    getUnavailableCoupon(
                        state.available,
                        [factors.shopId],
                        [{ shopId: factors.shopId, supplyStatus: factors.supplyStatus }],
                        [factors.payType],
                        [urlParams.id],
                        [factors.campaign]
                    )
                );
                const available = getAvailableCoupon(
                    state.available,
                    [factors.shopId],
                    [{ shopId: factors.shopId, supplyStatus: factors.supplyStatus }],
                    [factors.payType],
                    [urlParams.id],
                    [factors.campaign]
                );
                const sortedList = initCouponState(available, factors);
                return {
                    ...state,
                    unAvailable,
                    ...sortedList
                };
            }
            if (state.unAvailable) {
                const sortedList = initCouponState(state.available, factors);
                return {
                    ...state,
                    ...sortedList
                };
            }
            return state;
        }
        case CART_COUPON_INIT: {
            return {
                ...state,
                ...initCartCouponList(
                    action.payload,
                    state.available.concat(state.unAvailable || []).concat(state.otherCoupon || [])
                ),
                selected: state.selected
            };
        }
        case SET_BEST_CART_COUPON: {
            return {
                ...state,
                bestSelectedIds: action.payload,
                selectedIds: action.payload
            };
        }
        case SET_AVAILABLE_COUPON: {
            return {
                ...state,
                availableIds: action.payload
            };
        }
        default:
            return state;
    }
};

function initCouponState(list: ICouponApplied[], factors: CouponFactors) {
    const sortedList = computeMaximumCouponForGoodsList(list, {
        [factors.shopId]: [factors.goods]
    });

    // 之前的逻辑里没有添加没有选择的券无法使用的理由，比如说自动选择了店铺券，那么平台券未满足条件就不会被显示
    // 优惠券计算逻辑写的太乱了，都是直接在原数据上进行操作，参考购物车的优惠券初始化逻辑，所以选择了这个位置。
    const classifyCoupon = classifyCouponByShop(sortedList.available);
    if (classifyCoupon.platform) {
        getPlatformCoupon(classifyCoupon.platform, [factors.goods]);
    }
    Object.keys(classifyCoupon).forEach((shopId) => {
        if (shopId !== 'platform') {
            getShopCoupon(classifyCoupon[shopId], [factors.goods]);
        }
    });

    const coupon: Record<string, ICouponApplied | undefined> = {};
    if (sortedList.coupon) {
        Object.keys(sortedList.coupon).forEach((key) => {
            if (key !== 'platform') {
                coupon.shop = sortedList.coupon && sortedList.coupon[key];
            } else {
                coupon.platform = sortedList.coupon && sortedList.coupon[key];
            }
        });
    }
    return {
        available: sortedList.available,
        maxDiscount: { ...coupon },
        selected: { ...coupon }
    };
}

export const requestCouponInfo = () => {
    return apiActionWrapper(
        GET_COUPON_LIST.REQUEST,
        GET_COUPON_LIST.FAILURE,
        GET_COUPON_LIST.SUCCESS,
        get(`${API_EC}/coupon/canUsedCouponList?id=${urlParams.product_id || urlParams.id}`)
    );
};

export const requestCartCouponInfo = (ids: string) => {
    return apiActionWrapper(
        FETCH_CART_COUPON_LIST.REQUEST,
        FETCH_CART_COUPON_LIST.FAILURE,
        FETCH_CART_COUPON_LIST.SUCCESS,
        get(`${API_EC}/coupon/cart?str_shop_ids=${ids}`)
    );
};

export const updateCouponSelectChange = (coupon: ICouponApplied) => {
    return {
        type: UPDATE_COUPON_SELECT_CHANGE,
        payload: coupon
    };
};

export const updateConponSelect = (coupon?: ICouponApplied) => {
    return {
        type: UPDATE_COUPON_SELECT_CHANGE_NEW,
        payload: coupon
    };
};
export const updateCouponByBase = (data?: CouponFactors) => {
    return {
        type: COUPON_FACTORS_CHANGED,
        payload: data
    };
};
export const cartCouponInit = (data: {
    list: CartShop[];
    selected: string[];
    restrictIds?: string[];
    priceInfo?: PriceInfo;
}) => {
    return {
        type: CART_COUPON_INIT,
        payload: data
    };
};

export const setCartBestCoupon = (data: ISelectedCouponId) => {
    return {
        type: SET_BEST_CART_COUPON,
        payload: data
    };
};

export const setAvailableIds = (data: string[]) => {
    return {
        type: SET_AVAILABLE_COUPON,
        payload: data
    };
};
